import React, { useRef, useEffect, useState } from 'react';
import { TextField, Grid, Box, Typography } from '@mui/material';
import { ensureGooglePlacesLoaded } from '../utils/googlePlacesLoader';

const GOOGLE_PLACES_API_KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY || '';

export interface AddressValues {
  address_line1?: string;
  address_line2?: string;
  address_suburb?: string;
  address_state?: string;
  address_postcode?: string;
  address_country?: string;
  address_formatted?: string;
}

interface AddressAutocompleteProps {
  value: AddressValues;
  onChange: (address: AddressValues) => void;
  disabled?: boolean;
  line1Label?: string;
  /** Called when user selects an address from the Google dropdown (not when typing manually) */
  onPlaceSelected?: () => void;
}

declare global {
  interface Window {
    google?: {
      maps: {
        event?: {
          clearInstanceListeners: (instance: unknown) => void;
        };
        places: {
          Autocomplete: new (
            input: HTMLInputElement,
            opts?: { types?: string[]; componentRestrictions?: { country: string }; fields?: string[] }
          ) => {
            addListener: (event: string, fn: () => void) => void;
            getPlace: () => PlaceResult;
          };
        };
      };
    };
  }
}

interface PlaceResult {
  address_components?: { long_name: string; short_name: string; types: string[] }[];
  formatted_address?: string;
}

function parsePlaceResult(place: PlaceResult): AddressValues {
  const out: AddressValues = {};
  if (!place.address_components) return out;

  let streetNumber = '';
  let route = '';
  let locality = '';
  let state = '';
  let postcode = '';
  let country = '';

  for (const c of place.address_components) {
    if (c.types.includes('street_number')) streetNumber = c.long_name || '';
    if (c.types.includes('route')) route = c.long_name || '';
    if (c.types.includes('locality')) locality = c.long_name || '';
    if (c.types.includes('administrative_area_level_1')) state = c.short_name || c.long_name || '';
    if (c.types.includes('postal_code')) postcode = c.long_name || '';
    if (c.types.includes('country')) country = c.long_name || '';
  }

  out.address_line1 = [streetNumber, route].filter(Boolean).join(' ') || undefined;
  out.address_suburb = locality || undefined;
  out.address_state = state || undefined;
  out.address_postcode = postcode || undefined;
  out.address_country = country || undefined;
  out.address_formatted = place.formatted_address || undefined;
  return out;
}

export default function AddressAutocomplete({
  value,
  onChange,
  disabled = false,
  line1Label = 'Address',
  onPlaceSelected,
}: AddressAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  /** Latest structured values — avoids stale merges in Places `place_changed` (effect deps omit value/onChange). */
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onPlaceSelectedRef = useRef(onPlaceSelected);
  onPlaceSelectedRef.current = onPlaceSelected;

  const [scriptLoaded, setScriptLoaded] = useState(false);
  /** True when the search input is in the DOM; attach Autocomplete once per mount (ref avoids effect churn). */
  const [inputReady, setInputReady] = useState(false);
  const placesAttachedRef = useRef(false);
  /** When true, API key was rejected or script failed – show manual fields only, no broken search input */
  const [googleLoadError, setGoogleLoadError] = useState(false);

  useEffect(() => {
    if (!GOOGLE_PLACES_API_KEY) {
      setScriptLoaded(true);
      return;
    }
    let cancelled = false;
    ensureGooglePlacesLoaded().then((ok) => {
      if (cancelled) return;
      setScriptLoaded(true);
      if (!ok) setGoogleLoadError(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (
      !scriptLoaded ||
      !GOOGLE_PLACES_API_KEY ||
      googleLoadError ||
      !inputReady ||
      !inputRef.current ||
      placesAttachedRef.current
    ) {
      return;
    }
    const g = window.google;
    if (!g?.maps?.places?.Autocomplete) return;

    type AutocompleteInstance = {
      addListener: (event: string, fn: () => void) => void;
      getPlace: () => PlaceResult;
    };
    let autocomplete: AutocompleteInstance | null = null;

    try {
      const ac = new g.maps.places.Autocomplete(inputRef.current, {
        types: ['address'],
        componentRestrictions: { country: 'au' },
        fields: ['address_components', 'formatted_address'],
      });
      autocomplete = ac;

      ac.addListener('place_changed', () => {
        const place = ac.getPlace() as PlaceResult;
        if (place && place.address_components) {
          const next = { ...valueRef.current, ...parsePlaceResult(place) };
          onChangeRef.current(next);
          onPlaceSelectedRef.current?.();
          const el = inputRef.current;
          if (el && place.formatted_address) {
            el.value = place.formatted_address;
          }
        }
      });
      placesAttachedRef.current = true;
    } catch {
      placesAttachedRef.current = false;
      autocomplete = null;
      setGoogleLoadError(true);
    }

    return () => {
      if (autocomplete && g.maps.event?.clearInstanceListeners) {
        g.maps.event.clearInstanceListeners(autocomplete);
      }
      placesAttachedRef.current = false;
    };
  }, [scriptLoaded, GOOGLE_PLACES_API_KEY, googleLoadError, inputReady]);

  const handleFieldChange = (field: keyof AddressValues) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    onChange({ ...value, [field]: e.target.value || undefined });
  };

  const showSearchField = Boolean(GOOGLE_PLACES_API_KEY) && !googleLoadError;

  return (
    <Box>
      {showSearchField && (
        <TextField
          inputRef={(el) => {
            (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
            setInputReady(!!el);
          }}
          fullWidth
          label="Search address (Google)"
          placeholder="Start typing to search..."
          /* Uncontrolled: React controlled value fights google.maps.places.Autocomplete and breaks suggestions */
          defaultValue=""
          disabled={disabled}
          sx={{ mb: 2 }}
        />
      )}
      {googleLoadError && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Address search is unavailable. Please enter your address in the fields below.
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {showSearchField ? 'Structured address (editable)' : 'Address'}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={line1Label}
            value={value.address_line1 || ''}
            onChange={handleFieldChange('address_line1')}
            disabled={disabled}
            placeholder="Street number and name"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Line 2"
            value={value.address_line2 || ''}
            onChange={handleFieldChange('address_line2')}
            disabled={disabled}
            placeholder="Unit, building, etc. (optional)"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Suburb"
            value={value.address_suburb || ''}
            onChange={handleFieldChange('address_suburb')}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="State"
            value={value.address_state || ''}
            onChange={handleFieldChange('address_state')}
            disabled={disabled}
            placeholder="e.g. NSW"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Postcode"
            value={value.address_postcode || ''}
            onChange={handleFieldChange('address_postcode')}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Country"
            value={value.address_country || 'Australia'}
            onChange={handleFieldChange('address_country')}
            disabled={disabled}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
